import { useContext } from "react";
import { instanceOf, node } from "prop-types";
import { action, computed, observable } from "mobx";
import { DEFAULT_CURRENCY_KEY, DescriptionModes, Tables, getCurrency, unzip } from "@repo/utils";
import { StructureModel } from "@repo/structure";
import { getCurrencyExchangeRatesQuery, notifyAboutClientCommentQuery } from "@query";
import {
  WT_STATUS,
  VERTICAL_PADDING,
  HORIZONTAL_PADDING,
  SCALE_PRESETS,
  PROPOSAL_THUMB_ID,
  PAGE_VISIBLE_BOTTOM_OFFSET,
  PROJECT_TYPE,
  TABLE_CONTAINER_ID,
  PDF_DOCUMENT_CLASS,
  PROJECT_STATUS,
  PROJECT_COLUMNS,
  DEFAULT_WORK_TYPE_COLOR,
  DEFAULT_LABEL_COLORS,
  DEFAULT_LABEL_FONT_COLORS,
  ShareLinkModifiers,
  LabelGroups,
  universalDateParser,
  parseUsedPages,
  parseCompanySettings,
  getPdfPage,
} from "@utils";
import { EstimateEditorStoreContext } from "./EstimateEditorStoreContext"

// =================== Context, Provider & Hook ===================
export const EstimateEditorStoreProvider = ({ children, value }) => (
  <EstimateEditorStoreContext.Provider value={value}>
    {children}
  </EstimateEditorStoreContext.Provider>
);

export const useEstimateEditorStore = () => {
  const context = useContext(EstimateEditorStoreContext);
  if (context === null) {
    throw new Error("EstimateEditorStoreContext cannot be null, please add a context provider");
  }
  return context;
};


// =================== Store ===================
export class EstimateEditorStore {
  constructor({
    estimateType,
    estimateData,
    workTypes,
    companyLabels,
    customLabels,
    teamMembers,
    logo,
    unzipped,
    isSharedProposal,
    isPdfPrinter,
    printerPricesVisible,
    useHistoryManager,
    btIssues,
    companySettings,
    isCreated,
    companyLogo,
    defaultEstimateVersionsForPreview,
    groupedComments,
    defaultVersionKey,
    navigateFunc,
  }) {
    this.isStandardEstimate = estimateType === PROJECT_TYPE.STANDARD;
    if(isSharedProposal) this.isSharedProposal = true;
    if(logo) this.setCompanyLogo(logo);
    if(useHistoryManager) this.useHistoryManager = useHistoryManager;
    if(workTypes) this.setWorkTypes(workTypes);
    if(companyLabels) this.companyLabels = companyLabels;
    if(customLabels) this.customLabels = customLabels;
    if(companySettings) this.setCompanySettings(companySettings);
    if(companyLogo) this.setCompanyLogo(companyLogo);
    if(estimateData)
      this.setEstimateData({
        estimateData,
        isSharedProposal,
        isPdfPrinter,
        printerPricesVisible,
        unzipped,
        btIssues,
        defaultVersionKey,
        navigateFunc,
      });
    if(teamMembers) this.setTeamMembers(teamMembers);
    if(groupedComments)
      this.groupedComments = groupedComments;
    this.isCreated = Boolean(isCreated);

    if(defaultEstimateVersionsForPreview) {
      this.estimateVersions = defaultEstimateVersionsForPreview;
      this.currentVersion = defaultEstimateVersionsForPreview[0].key;
      if(groupedComments)
        defaultEstimateVersionsForPreview[0].structure.historyManager?.withoutUndo(() => {
          defaultEstimateVersionsForPreview[0].structure.setComments(groupedComments);
        })
    }
  }

  // =============== PROPERTIES ===============
  isSharedProposal;
  blockedAverageAlert;
  blockedRiskAlert;
  blockedResizeAlert;
  blockedHideTimelineSectionAlert;
  blockedValueAlert;
  hadDifferentValues;
  useHistoryManager;
  defaultSalesCurrency = DEFAULT_CURRENCY_KEY;
  defaultCostCurrency = DEFAULT_CURRENCY_KEY;

  @observable accessor salesRateAlertLock = true;
  @observable accessor salesRateAlertAction = false;
  @observable accessor importRoleSalesRates = true;
  @observable accessor importRoleCostRates = true;

  @observable accessor teamMembers = [];

  @observable accessor estimateLoaded = false;
  @observable accessor estimateName;
  @observable accessor estimateDesc;
  @observable accessor estimateDue;
  @observable accessor estimateCategory;
  isStandardEstimate;
  @observable accessor isCreated;
  @observable accessor isRestored;

  @observable accessor proposalStep = 0;

  @observable accessor estimateStatus;
  @observable accessor estimateUsers;
  @observable accessor estimateId;
  @observable accessor estimateUserWorkTypeVisibility = {};
  @observable accessor companyLogo = null;
  @observable accessor shareLinkSettings = null;

  @observable accessor metadataWorkTypes = [];
  @observable accessor workTypes = [];
  @observable accessor customLabels = [];
  @observable accessor companyLabels = [];
  @observable accessor additionalColors = [];
  @observable accessor additionalFontColors = [];
  @observable accessor workTypesWithStatus = [];

  @observable accessor shareModalForced = false;

  @observable accessor estimateVersions = [];
  @observable accessor currentVersion;
  @observable accessor newVersionLoading;

  savedExchangeRates = [];
  
  renderedTextDescription = "";
  
  @observable accessor visibilityMode = false;
  @observable accessor allowVisibility = false;
  @observable accessor hasCreatedDescription = false;
  
  @observable accessor profitabilityMode = false;
  @observable accessor allowProfitability = false;
  
  @observable accessor allowedInternalComments = false;
  @observable accessor addedCommentThisSession = false;
  
  // @observable accessor currencyExchangeRate = 1;

  // ----------- PDF -----------

  @observable accessor pdfDocument;
  @observable accessor newPdfFile = false;
  @observable accessor currentPage = 0;
  @observable accessor totalPages = 0;
  @observable accessor allPdfPages = [];
  @observable accessor usedPdfPages = [];
  @observable accessor proposalTablePosition;
  basePagesUrl;
  documentChanged;

  containerRef = null;
  pageViewRef = null;
  thumbViewRef = { current: null };
  
  versionScroll = {};

  @observable accessor displayScale = 1;
  @observable accessor scale = 1;
  @observable accessor scaleValue = "page-fit";
  @observable accessor appliedScrollPosition;
  
  @observable accessor displayedComment;
  @observable accessor isAiGenerating;
  @observable accessor showAiGenerationAlert;
  @observable accessor aiGenerationSuccessAlert;
  @observable accessor aiGenerationFileStatus;
  
  @observable accessor unsupportedBrowserAlert = false;
  
  /** an MCP agent edits the project ({ actorName, tool, startedAt, idleTtlSeconds }), the editor is read-only */
  @observable accessor agentSession = null;

  // =============== COMPUTED-S ===============
  
  @computed get isAgentWorking() {
    return Boolean(this.agentSession);
  }

  @computed get isArchived() {
    return this.estimateStatus === PROJECT_STATUS.ARCHIVE;
  }
  
  @computed get isRemoved() {
    return this.estimateStatus === PROJECT_STATUS.REMOVED;
  }
  
  @computed get presetEditionMode() {
    return this.proposalStep === 1;
  }

  @computed get currentEstimateStructure() {
    return this.estimateVersions?.slice()?.find((v) => v.key === this.currentVersion)
      ?.structure;
  }
  
  @computed get currentVersionKey() {
    return this.estimateVersions?.slice()?.find((v) => v.key === this.currentVersion)
      ?.key;
  }
  
  @computed get currentVersionName() {
    return this.estimateVersions?.slice()?.find((v) => v.key === this.currentVersion)
      ?.name;
  }

  @computed get currentVersionVisible() {
    return Boolean(
      this.estimateVersions?.slice()?.find((v) => v.key === this.currentVersion)?.visible
    );
  }
  
  @computed get currentVersionLocked() {
    return Boolean(
      this.estimateVersions?.slice()?.find((v) => v.key === this.currentVersion)?.locked
    );
  }
  
  @computed get sortedVersions() {
    return this.estimateVersions
      .slice()
      .sort((a, b) => (a.order > b.order ? 1 : -1));
  }
  
  @computed get visibleEstimateVersions() {
    return this.estimateVersions.filter(
      ({ visible }) => visible
    ).length;
  }

  @computed get pdfPages() {
    return this.usedPdfPages
      .slice()
      .sort((a, b) => (a[1] > b[1] ? 1 : -1))
      .map(([p]) => p);
  }

  pageMapFunc = (p) => this.allPdfPages[p - 1] || { id: p };
  @computed get pdfPageThumbnails() {
    return this.isStandardEstimate
      ? [
          ...this.headerPages.map(this.pageMapFunc),
          { id: PROPOSAL_THUMB_ID },
          ...this.footerPages.map(this.pageMapFunc),
        ]
      : this.pdfPages.map(this.pageMapFunc);
  }

  @computed get numPages() {
    return this.pdfPageThumbnails.length;
  }

  @computed get headerPages() {
    if (this.isStandardEstimate)
      return this.pdfPages.slice(0, this.proposalTablePosition);

    return this.pdfPages;
  }

  @computed get footerPages() {
    if (this.isStandardEstimate)
      return this.pdfPages.slice(this.proposalTablePosition);

    return [];
  }

  @computed get pdfPagesLoaded() {
    return (
      !this.pdfDocument ||
      (this.totalPages > 0 &&
        this.allPdfPages.filter((p) => !!p).length === this.totalPages)
    );
  }

  @computed get widestPageSize() {
    let width = 0,
      height = 0;

    this.allPdfPages.forEach((page) => {
      if (!page) return;
      if (page.originalWidth > width) {
        width = page.originalWidth;
        height = page.originalHeight;
      }
    });

    return { width, height };
  }
  
  @computed get nonOwnerTeamMembers() {
    return this.teamMembers.filter(user => !(
      (this.estimateUsers?.author && user.uuid === this.estimateUsers?.author?.uuid) ||
      (this.estimateUsers?.owner && user.uuid === this.estimateUsers?.owner?.uuid)
    ))
  }
  
  @computed get noTurningOff() {
    return this.shareLinkSettings?.[ShareLinkModifiers.NO_TURN_OFF] || false;
  }
  
  @computed get hideTurnedOffElements() {
    return this.shareLinkSettings?.[ShareLinkModifiers.HIDE_TURN_OFF] || false;
  }
  
  @computed get hideBottomBar() {
    return this.shareLinkSettings?.[ShareLinkModifiers.HIDE_BOTTOM_BAR] || false;
  }
  
  @computed get hideExports() {
    return this.shareLinkSettings?.[ShareLinkModifiers.HIDE_EXPORTS] || false;
  }

  @computed get defaultSalesCurrencyObj() {
    return getCurrency(this.defaultSalesCurrency);
  }

  @computed get defaultCostCurrencyObj() {
    return getCurrency(this.defaultCostCurrency);
  }
  
  // =============== GETTERS ===============
  
  hasAssignedWorkTypes(userUuid) {
    return Boolean(this.workTypesWithStatus?.find(w => w.estimatorUuid === userUuid));
  }

  getPageImageUrl(pageNum) {
    return `${this.basePagesUrl}_${pageNum}.png`;
    // const s = decodeURIComponent(this.basePagesUrl).split("/");
    // s[s.length-1] = encodeURIComponent(s[s.length-1]);
    // return `${s.join("/")}_${pageNum}.png`
  }
  
  getEstimateUser(userUuid) {
    return this.estimateUsers.observer.find(u => u.uuid === userUuid);
  }
  
  isEstimateUser(userUuid) {
    return Boolean(this.estimateUsers.observer.find(u => u.uuid === userUuid))
  }

  getEstimateId() {
    return this.estimateData;
  }

  // =============== ACTIONS ===============
  
  setCompanySettings({
    projectAverageAlert,
    // projectRiskBufferAlert,
    projectResizeAlert,
    hideTimelineSectionAlert,
    projectValueAlert,
    hadDifferentValues,
    isTimelineAllowed,
    isTextDescriptionAllowed,
    dayModifier,
    defaultCurrency,
    companyCurrency,
  }) {
    if (projectAverageAlert) this.blockedAverageAlert = projectAverageAlert;
    // if (projectRiskBufferAlert) this.blockedRiskAlert = projectRiskBufferAlert;
    if (projectResizeAlert) this.blockedResizeAlert = projectResizeAlert;
    if (hideTimelineSectionAlert) this.blockedHideTimelineSectionAlert = hideTimelineSectionAlert;
    if (projectValueAlert) this.blockedValueAlert = projectValueAlert;
    if (hadDifferentValues) this.hadDifferentValues = hadDifferentValues;
    if (isTimelineAllowed) this.isTimelineAllowed = isTimelineAllowed;
    if (isTextDescriptionAllowed) this.isTextDescriptionAllowed = isTextDescriptionAllowed;
    if (defaultCurrency) this.defaultSalesCurrency = parseCompanySettings(defaultCurrency);
    if (companyCurrency) this.defaultCostCurrency = parseCompanySettings(companyCurrency);
    if (dayModifier) this.dayModifier = dayModifier;
    if (defaultCurrency) this.defaultSalesCurrency = defaultCurrency;
    if (companyCurrency) this.defaultCostCurrency = companyCurrency;
  }
  
  @action switchSalesRateAlertLock() {
    this.salesRateAlertLock = !this.salesRateAlertLock;
  }
  @action setCreatedDescription = (created) => {
    this.hasCreatedDescription = created;
  };
  @action setSalesRateAlertAction(action) {
    this.salesRateAlertAction = action;
  }
  @action switchRoleSalesRatesImport() {
    this.importRoleSalesRates = !this.importRoleSalesRates;
  }
  @action switchRoleCostRatesImport() {
    this.importRoleCostRates = !this.importRoleCostRates;
  }

  
  @action setTeamMembers = (teamMembers) => {
    this.teamMembers = teamMembers;
  };

  @action setEstimateName = (name) => (this.estimateName = name);

  @action setEstimateStatus(status) {
    this.isRestored = this.estimateStatus === PROJECT_STATUS.ARCHIVE && Object.values(PROJECT_COLUMNS).includes(status);
    return (this.estimateStatus = status);
  }
  
  @action setRestored() {
    this.isRestored = undefined;
  }

  @action setWorkTypes = (workTypes) => {
    this.workTypes = workTypes;
  };

  @action addAdditionalColor = (color) => {
    if (color && !DEFAULT_LABEL_COLORS.includes(color) && !this.additionalColors.includes(color))
      this.additionalColors = [...this.additionalColors, color];
  };

  @action addAdditionalFontColor = (color) => {
    if (color && !DEFAULT_LABEL_FONT_COLORS.includes(color) && !this.additionalFontColors.includes(color))
      this.additionalFontColors = [...this.additionalFontColors, color];
  };

  @action addCustomEstimateWorkTypes = (workTypes, customWorkTypesWereCreated) => {
    const ids = this.workTypes
      .filter(wT => !wT.custom)
      .map(w => w.id);
    this.metadataWorkTypes = workTypes;
    this.workTypes = [
      ...this.workTypes.filter(wT => !wT.custom),
      ...workTypes
        .filter(wT => !ids.includes(wT.id) && !ids.includes(wT.systemId))
        .sort((a,b) => Math.abs(a.id) > Math.abs(b.id) ? 1 : -1)
        .map(wT => ({
          ...wT,
          id: customWorkTypesWereCreated
            ? wT.systemId ?? wT.id
            : wT.id,
          custom: customWorkTypesWereCreated
            ? !wT.systemId
            : true,
          backgroundColor: wT.backgroundColor || DEFAULT_WORK_TYPE_COLOR
        }))
    ];
  };
  
  @action addCustomLabel(label) {
    this.customLabels.push(label);
  }
  @action addCompanyLabel(label) {
    this.companyLabels.push(label);
  }
  @action removeCompanyLabel(labelId) {
    const label = this.companyLabels.find(l => l.id !== labelId);
    if(!label)
      return;
    this.companyLabels = this.companyLabels.filter(l => l.id !== labelId);
    return {...label}
  }
  @action removeCustomLabel(labelId) {
    this.customLabels = this.customLabels.filter(l => l.id !== labelId);
  }
  @action updateCustomLabel(labelId, labelData) {
    const labels = [...this.customLabels];
    const index = labels.findIndex(l => l.id === labelId);
    if(index < 0)
      return;
    labels[index] = { ...labels[index], ...labelData };
    this.customLabels = labels;
  }
  @action updateCompanyLabel(labelId, labelData) {
    const labels = [...this.companyLabels];
    const index = labels.findIndex(l => l.id === labelId);
    if(index < 0)
      return;
    labels[index] = { ...labels[index], ...labelData };
    this.companyLabels = labels;
  }
  
  @action addUnlistedLabels(labels) {
    const list = labels
      .filter(l => (
        !this.customLabels.find(cL => cL.id === l.systemId)
        && !this.companyLabels.find(cL => cL.id === l.systemId)
      ))
      .map(l => ({
        ...l,
        group: LabelGroups.UNLISTED
      }))
    if(!list.length) return;
    this.customLabels.push(...list);
  }

  @action setEstimateUsers = (users) => {
    this.estimateUsers = users;
  };
  
  @action setEstimateUserWorkTypeVisibility = (userId, status) => {
    this.estimateUserWorkTypeVisibility = {
      ...this.estimateUserWorkTypeVisibility,
      [userId]: status ? 1 : 0
    };
  };

  @action setCompanyLogo = (companyLogo) => {
    this.companyLogo = companyLogo;
  };
  
  @action async getExchangeRates(...currencyCodes) {
    let missingRates = currencyCodes.filter(code => !this.savedExchangeRates.find(r => r.code === code));
    if(missingRates.length) {
      try {
        const rates = await getCurrencyExchangeRatesQuery(...missingRates);
        this.savedExchangeRates.push(...rates);
      } catch(e) {
        console.error(e);
      }
    }

    return this.savedExchangeRates;
  }

  @action async handleCurrentEstimateStructureProfitabilityMode() {
    if(!this.currentEstimateStructure || this.currentEstimateStructure.settings.useCustomExchangeRate)
      return;
    
    if(!this.currentEstimateStructure.settings.hasOutdatedDailyExchangeRate()) {
      const savedDailyRates = this.currentEstimateStructure.settings.getSavedDailyRates();
      
      if(savedDailyRates?.length) {
        let newRates = savedDailyRates.filter(({ code }) => !this.savedExchangeRates.find(r => r.code === code));
        this.savedExchangeRates.push(...newRates);
        return;
      }
    }

    try {
      const { companyCurrency, currency } = this.currentEstimateStructure.settings;
      const rates = await this.getExchangeRates(companyCurrency, currency);
      this.currentEstimateStructure.settings.setDailyExchangeRate(rates);
      
      if(!this.currentEstimateStructure.usesNewPA)
        this.currentEstimateStructure.oneTimeReplaceCompanyUnitCost();
    } catch(e) {
      if(!this.currentEstimateStructure.settings.dailyExchangeRate)
        this.currentEstimateStructure.settings.setDefaultDailyExchangeRate();
    }
  }

  @action async switchProfitabilityMode() {
    if(!this.profitabilityMode)
      await this.handleCurrentEstimateStructureProfitabilityMode();
    
    this.profitabilityMode = !this.profitabilityMode;
    if (this.visibilityMode) this.visibilityMode = false;
  };
  
  @action setAllowProfitability(allowProfitability) {
    this.allowProfitability = allowProfitability
  }
  
  @action setAllowVisibility(allowVisibility) {
    this.allowVisibility = allowVisibility
  }

  @action setShareLinkSettings(key, value) {
    if(!this.shareLinkSettings)
      this.shareLinkSettings = {};
    
    this.shareLinkSettings[key] = value;
  }
  @action setAllShareLinkSettings(settings) {
    this.shareLinkSettings = settings;
  }
  //
  // @action setCurrencyExchangeRate = (
  //   companyCurrencyRate,
  //   estimateCurrencyRate
  // ) => {
  //   this.currencyExchangeRate = companyCurrencyRate * (1 / estimateCurrencyRate);
  // };

  @action setCreatedStatus(status) {
    this.isCreated = status;
  }
  
  @action setRenderedTextDescription(renderedTextDescription) {
    this.renderedTextDescription = renderedTextDescription;
  }

  @action cleanup = () => {
    this.setProposalStep(0);
    this.profitabilityMode = false;
    this.visibilityMode = false;

    this.estimateVersions = [];
    this.currentVersion = undefined;

    this.estimateDesc = undefined;
    this.estimateDue = undefined;
    this.estimateStatus = undefined;
    this.estimateCategory = undefined;
  };

  @action forceShareModal = (state) => {
    this.shareModalForced = state;
  };

  @action setProposalStep = (v, m) => {
    const newStep = v !== null ? v : this.proposalStep + m;
    
    if(newStep === 2 && typeof Promise.withResolvers !== "function") {
      this.unsupportedBrowserAlert = true;
      return;
    }
    
    this.proposalStep = newStep;
    this.visibilityMode = false;
    this.profitabilityMode = false;
    
    this.estimateVersions.forEach(ver => {
      ver.structure?.historyManager.withoutUndo(() => {
        ver.structure?.setHideEmptySectionDescriptions(this.proposalStep > 0);
        ver.structure?.visibility?.applyVisibility(this.proposalStep > 0);
        ver.structure?.setExcludeTurnedOffElements(this.proposalStep > 0 && this.shareLinkSettings?.[ShareLinkModifiers.HIDE_TURN_OFF]);
      })
    });
    
  };

  @action useVisibilityMode = (use) => {
    this.visibilityMode = use;
    if (this.profitabilityMode) this.profitabilityMode = false;
    
    this.currentEstimateStructure.historyManager.withoutUndo(() => {
      this.estimateVersions.forEach(( ver ) => {
        ver.structure?.visibility?.previewVisibility(use);
      });
    });
  };

  @action
  setEstimateData({
    estimateData,
    isSharedProposal = false,
    isPdfPrinter = false,
    printerPricesVisible = false,
    unzipped = false,
    keepStructures = false,
    btIssues = null,
    defaultVersionKey,
    navigateFunc,
  }) {
    // this.estimateLoaded = false;
    const {
      // uuid,
      name,
      content,
      tags,
      versions,
      category,
      logo,
      dueOn,
      status,
      users,
      visible,
      cover,
      covers,
      timeline,
      textDescription,
      shareLinkSettings,
      shareSettings,
    } = estimateData;
    this.setWorkTypesWithStatus(tags);

    if (name) this.estimateName = name;
    if (logo) this.companyLogo = logo;
    if (shareLinkSettings && typeof shareLinkSettings === "string")
      this.shareLinkSettings = JSON.parse(shareLinkSettings);
    else if (shareSettings && typeof shareSettings === "string")
      this.shareLinkSettings = JSON.parse(shareSettings);
    
    if(versions?.length) {
      if(isSharedProposal || btIssues !== null) {
        const parsedVer = versions.map(( v ) => this.unzipVersion(v, !timeline/* || isPdfPrinter*/, !textDescription, isPdfPrinter && !printerPricesVisible, unzipped))
        const hasVisibleVersion = parsedVer.find(v => v?.visible);
        const smallestOrder = parsedVer.reduce(( min, v ) => Math.min(v.order, min), Infinity);
        
        this.estimateVersions = parsedVer.filter(v => (!hasVisibleVersion && v.order === smallestOrder) || v?.visible);
        
        if ( this.currentVersion === undefined ) {
          if(defaultVersionKey && this.sortedVersions.find(v => v.key === Number(defaultVersionKey)))
            this.currentVersion = Number(defaultVersionKey);
          else
            this.currentVersion = this.sortedVersions[ 0 ].key;
          
          navigateFunc?.(this.currentVersion);
        }
        
        if (btIssues !== null) {
          this.currentVersion = btIssues.version;
          const versionIndex = versions.findIndex(
            (item) => item.key === this.currentVersion
          );
          const arrayIdWT = Object.values(btIssues.workTypes);
          
          let path;
          
          if (btIssues.issues[0].workTypes["-200"] >= 0) {
            const usedWorkTypes = this.estimateVersions[
              versionIndex
              ].structure.workTypes.filter((workType) =>
              arrayIdWT.includes(workType.id)
            );
            
            const sumWTRate = usedWorkTypes.reduce((totalRate, workType) => {
              return (
                totalRate +
                workType.rate *
                this.estimateVersions[versionIndex].structure.settings.modifier
              );
            }, 0);
            
            const averageWT =
              sumWTRate /
              usedWorkTypes.length /
              this.estimateVersions[versionIndex].structure.settings.modifier;
            
            const nameWorkTypes = usedWorkTypes
              .map((workType) => {
                return workType.name;
              })
              .join(", ");
            this.estimateVersions[versionIndex].structure.addWorkType(
              false,
              {
                id: -200,
                name: nameWorkTypes,
              },
              averageWT
            );
            this.estimateVersions[versionIndex].structure.settings.setBtWorkTypes(
              arrayIdWT
            );
            this.estimateVersions[versionIndex].structure.sections.forEach(
              (section) => {
                section.addWorkType(-200);
              }
            );
          } else {
            this.estimateVersions[versionIndex].structure.workTypes.forEach(
              (wT) => {
                if (!arrayIdWT.includes(wT.id)) wT.setTurnOffState(true);
              }
            );
          }
          
          this.estimateVersions[versionIndex].structure.sections.forEach((sec) => {
            if (!btIssues.sections.includes(sec.id)) sec.setTurnOffState(true);
          });
          btIssues.issues.forEach((item) => {
            path = `/sections/` + item.index.replace(/\./g, `/children/`);
            const el = path
              .split("/")
              .splice(1)
              .reduce(
                (node, key) => node[key],
                this.estimateVersions[versionIndex].structure
              );
            el.setBTValues(item.workTypes);
          });
          
          btIssues.newIssues.forEach((item) => {
            this.estimateVersions[versionIndex].structure.attachSection(item);
          });
          
          if (typeof btIssues.depth === "number")
            this.estimateVersions[versionIndex].structure.settings.setValueLevel(
              btIssues.depth
            );
        }
        
        if(this.shareLinkSettings?.[ShareLinkModifiers.HIDE_TURN_OFF])
          this.estimateVersions.forEach(ver => {
            ver.structure?.historyManager.withoutUndo(() => {
              ver.structure?.setExcludeTurnedOffElements(true);
            })
          });
        
        if(isPdfPrinter) {
          this.estimateVersions.forEach(ver => {
            if(!ver.structure)
              return;
            
            if(ver.structure.settings.descriptionMode === DescriptionModes.TOOLTIP)
              ver.structure.historyManager.withoutUndo(() => {
                ver.structure.settings.setDescriptionMode(DescriptionModes.ROW);
              })
          });
        }
      }
      else {
        this.estimateVersions = versions.map(v => ({
          ...v,
          structure: keepStructures
            ? this.estimateVersions.find(ve => ve.key === v.key)?.structure
            : undefined
        }));
      }
    }

    if ((!isSharedProposal || cover) && covers?.[0]) {
      this.setPdfDocument(covers[0]);
    }

    this.estimateDue = dueOn && universalDateParser(dueOn);
    this.estimateStatus = status;
    this.estimateDesc = content;
    this.estimateCategory = category;
    this.estimateUsers = users;
    this.estimateId = estimateData.id;
    this.estimateUserWorkTypeVisibility = visible || {};
    this.estimateLoaded = true;
  }


  @action setVersions(versions) {
    this.estimateVersions = versions.map(v => ({
      ...v,
      structure: this.estimateVersions.find(ve => ve.key === v.key)?.structure,
      metadata: typeof v.metadata === "string" && v.metadata?.length
        ? JSON.parse(v.metadata)
        : v.metadata,
    }));
  }

  @action setVersionData(versionData, noPrices) {
    const index = this.estimateVersions?.findIndex((v) => v.key === versionData.key)
    if(index >= 0) {
      const unzipped = this.unzipVersion(
        {
          ...versionData }, !this.isSharedProposal && !this.isTimelineAllowed,
        !this.isSharedProposal && !this.isTextDescriptionAllowed,
        noPrices
      );
      
      this.estimateVersions[ index ].structure = unzipped.structure;
      this.estimateVersions[ index ].metadata = unzipped.metadata;
      if(versionData.name)
        this.estimateVersions[ index ].name = versionData.name;
      
      this.estimateVersions[ index ].structure.historyManager.withoutUndo(() => {
        const comments = this.groupedComments?.filter(group => group.version = versionData.key);
        if(comments?.length) {
          this.estimateVersions[ index ].structure.setComments(comments);
        }
        
        if(this.displayedComment && this.displayedComment.version === versionData.key) {
          this.estimateVersions[ index ].structure.openByPath(this.displayedComment.path);
        }
      })
      
      let currentIndex;
      if(this.currentVersion !== versionData.key)
        currentIndex = this.estimateVersions?.findIndex((v) => v.key === this.currentVersion)
      
      this.setCurrentVersion(versionData.key);
      if(typeof currentIndex === "number" && this.estimateVersions[ currentIndex ]) {
        delete this.estimateVersions[ currentIndex ].structure
      }
    }
  }
  
  @action unzipVersion(
    { structure, metadata, ...versionData },
    noTimeline = false,
    noTextDescription = false,
    noPrices = false,
    unzipped = false
    // ignoreVisibility = false
  ) {
    try {
      if(!structure)
        return versionData;
      
      const p = unzipped ? structure : JSON.parse(unzip(structure));
      // if(isSharedProposal && !p?.visibility?.version && !ignoreVisibility)
      //   return;
      
      if(noPrices)
        p.hideAllPrices = true;
      
      const data = {
        ...versionData,
        structure: StructureModel.create(p),
        metadata: typeof metadata === "string" && metadata?.length
          ? JSON.parse(metadata)
          : metadata,
      };

      Object.values(data.structure.timelines || {}).forEach((timeline) => {
        timeline?.sections?.forEach((section) => {
          this.addAdditionalColor(section.color);
          this.addAdditionalFontColor(section.fontColor);
          section.workTypes?.forEach((workType) => {
            this.addAdditionalColor(workType.color);
            this.addAdditionalFontColor(workType.fontColor);
            workType.teamMembers?.forEach((teamMember) => {
              this.addAdditionalColor(teamMember.color);
              this.addAdditionalFontColor(teamMember.fontColor);
            });
          });
        });
      });
      
      if (noTimeline)
        data.structure.settings?.usedTables
          ?.find((t) => t.name === Tables.TIMELINE)
          ?.setVisibility(false);
      
      if (noTextDescription)
        data.structure.settings?.usedTables
          ?.find((t) => t.name === Tables.DESCRIPTION)
          ?.setVisibility(false);
      
      if (this.useHistoryManager) data.structure.initHistoryManager();
      
      return data;
    } catch(error) {
      throw {
        versionData,
        error,
      }
    }
  }

  @action addCustomWorkType(id, name, backgroundColor=DEFAULT_WORK_TYPE_COLOR, wasCreated=false) {
    this.workTypes.push({
      id,
      name,
      backgroundColor,
      custom: !wasCreated,
      estimator: null,
      estimatorUuid: null,
      status: WT_STATUS.PENDING,
    });
    // this.addWorkTypeWithStatus({
    //   id,
    //   name,
    //   backgroundColor,
    // });
    this.addWorkTypeWithStatus(id);
  }
  
  @action removeWorkType(id) {
    // if(this.metadataWorkTypes.find(wT => wT.id === id))
    //   return;
    
    this.workTypes = this.workTypes.filter(wT => wT.id !== id);
  }
  
  @action updateCustomWorkTypeIds(workTypeIds) {
    const l = [...this.workTypes];
    workTypeIds.forEach(({ oldId, newId }) => {
      const match = l.find(wT => wT.id === oldId);
      if(match) {
        match.id = newId;
        delete match.custom;
      }
    })
    
    this.workTypes = l;
  }
  
  @action updateWorkTypeData(id, name, backgroundColor) {
    const l = [...this.workTypes];
    const match = l.find(wT => wT.id === id);
    if(!match)
      return;
    
    match.name = name;
    match.backgroundColor = backgroundColor;
    this.workTypes = l;
  }

  // ----------- VERSIONS -----------

  @action setNewVersionLoading() {
    this.newVersionLoading = true;
  }

  @action addNewVersion(versionData, focus = false) {
    const version = this.unzipVersion(versionData);
    this.estimateVersions.push(version);
    this.newVersionLoading = false;
    if (focus) this.setCurrentVersion(version.key);
  }

  @action async setCurrentVersion(versionKey) {
    if(!this.estimateVersions.find((v) => v.key === versionKey))
      return;
    this.currentVersion = versionKey;
    if(this.profitabilityMode)
      await this.handleCurrentEstimateStructureProfitabilityMode();
  }

  @action editVersionName(versionKey, name) {
    const version = this.estimateVersions.find((v) => v.key === versionKey);
    version.name = name;
    // version.structure.setVersionName(name);
  }

  @action setVersionHidden(versionKey, visible) {
    const version = this.estimateVersions.find((v) => v.key === versionKey);
    version.visible = visible;
  }
  
  @action setEditionLock(versionKey, locked) {
    const version = this.estimateVersions.find((v) => v.key === versionKey);
    version.locked = locked;
  }

  @action removeSelectedVersion(versionKey) {
    const index = this.estimateVersions.findIndex((v) => v.key === versionKey);

    this.estimateVersions.splice(index, 1);
    // if (this.currentVersion === versionKey) {
    //   this.setCurrentVersion(
    //     this.estimateVersions[index === 0 ? index : index - 1].key
    //   );
    // }
  }

  @action updateVersionOrder(versionOrder) {
    this.estimateVersions.forEach((v) => {
      const o = versionOrder.find((vO) => vO.key === v.key);
      if(o) v.order = o.order;
    });
  }

  @action reorderVersions(list) {
    this.estimateVersions.forEach((v) => {
      v.order = list.indexOf(v.key) + 1;
    });
  }

  // ----------- ASSIGNMENTS -----------

  @action setWorkTypesWithStatus = (workTypes) => {
    this.workTypesWithStatus = workTypes || [];
  };
  @action addWorkTypeWithStatus = (workTypeId) => {
    const wT = this.workTypes.find((t) => t.id === workTypeId);
    if (!wT) return;

    this.workTypesWithStatus?.push({
      ...wT,
      estimator: null,
      estimatorUuid: null,
      status: WT_STATUS.PENDING,
    });
  };

  @action addLocalPermit(workTypeId, userUuid) {
    let workTypes = this.workTypesWithStatus.slice(0);
    let wT = workTypes.find((wT) => wT.id === workTypeId);
    if(!wT) {
      this.addWorkTypeWithStatus(workTypeId);
      workTypes = this.workTypesWithStatus.slice(0);
      wT = workTypes.find((wT) => wT.id === workTypeId);
    }
    wT.estimatorUuid = userUuid;
    this.workTypesWithStatus = workTypes;
  }
  @action removeLocalPermit(workTypeId) {
    const workTypes = this.workTypesWithStatus.slice(0);
    const wT = workTypes.find((wT) => wT.id === workTypeId);
    wT.estimatorUuid = undefined;
    this.workTypesWithStatus = workTypes;
  }

  @action updateLocalPermit(workTypeId, permit) {
    const workTypes = this.workTypesWithStatus.slice(0);
    const wT = workTypes.find((wT) => wT.id === workTypeId);
    wT.status =
      permit ||
      (wT.status === WT_STATUS.PENDING
        ? WT_STATUS.ESTIMATED
        : WT_STATUS.PENDING);
    this.workTypesWithStatus = workTypes;
    return wT.status;
  }

  // ----------- PDF -----------

  setContainerRef = (r) => (this.containerRef = r);
  setPageViewRef = (r) => (this.pageViewRef = r);
  setThumbViewRef = (r) => (this.thumbViewRef = { current: r });

  @action setPdfDocument = (document, newPdfFile = false) => {
    this.removeDocument();
    
    this.scale = Number(document.scale || 1);
    this.displayScale = Number(document.scale || 1);
    if (document.scale) this.scaleValue = SCALE_PRESETS.CUSTOM;
    this.newPdfFile = newPdfFile;
    this.pdfDocument = document;
    this.usedPdfPages = parseUsedPages(document.usedPages);
    this.proposalTablePosition = Number(document.tablePos);

    this.basePagesUrl = decodeURIComponent(document.file.path).match(
      /(https?:\/\/.*.s3.*\.com\/.*\/.*)\.pdf/
    )[1];
  };

  @action async setupDocument(data) {
    this.pdfDocument.fileData = await data.getData();
    this.totalPages = data.numPages;
    if (this.newPdfFile || !this.usedPdfPages.length) {
      const usedPdfPages = parseUsedPages("[]", data.numPages);
      this.usedPdfPages = usedPdfPages;
      this.newPdfFile = false;
      this.scaleValue = SCALE_PRESETS.FIT;
      this.setScale(undefined, SCALE_PRESETS.FIT);
      return { usedPdfPages, scale: this.scale };
    }
  }

  @action handlePageLoaded(pageNum, originalWidth, originalHeight) {
    this.allPdfPages[pageNum - 1] = {
      id: pageNum,
      originalWidth,
      originalHeight,
      el: getPdfPage(pageNum),
    };
  }

  @action setScale(value, scaleValue = undefined, noPageUpdate = false) {
    if (!this.pageViewRef || !this.pdfDocument) return;
    if (!value && this.scaleValue === SCALE_PRESETS.CUSTOM) return;
    if (value) this.documentChanged = true;

    if (!value) value = this.scaleValue;
    
    let scale = parseFloat(value);
    
    if (scale > 0) {
      if (scale < 0.1) scale = 0.1;
      if (scale > 10) scale = 10;
    } else {
      const { width, height } = this.widestPageSize;

      if (width === 0) return;

      const containerWidth =
        this.pageViewRef.clientWidth - 2 * HORIZONTAL_PADDING;
      const pageWidthScale = containerWidth / width;

      const pageHeightScale =
        (this.pageViewRef.clientHeight - 2 * VERTICAL_PADDING) / height;

      switch (value) {
        case SCALE_PRESETS.WIDTH:
          scale = pageWidthScale;
          break;
        case SCALE_PRESETS.FIT:
          scale = Math.min(pageWidthScale, pageHeightScale);
          break;
        case SCALE_PRESETS.AUTO:
          scale = Math.min(1.25, Math.min(pageHeightScale, pageWidthScale));
          break;
        default:
          console.error(`_setScale: "${value}" is an unknown zoom value.`);
          return this.pdfDocument;
      }
    }

    if (!noPageUpdate) this.applyScale(scale, scaleValue || value);
    this.displayScale = scale;

    this.pdfDocument.scale = parseFloat(value);
    this.findCurrentPage();
    return {
      scale,
      scaleValue: scaleValue || value,
    };
  }

  @action applyScale(scale, scaleValue) {
    let offsets = 4;
    const children = this.pageViewRef.querySelector(`.${PDF_DOCUMENT_CLASS}`).children;
    if(children) {
      for (const child of children) {
        if(child.offsetTop < this.pageViewRef.scrollTop)
          offsets += 4;
        if(child.offsetTop + child.offsetHeight < this.pageViewRef.scrollTop)
          offsets += 4;
      }
    }


    const diff = Number((scale / this.scale).toPrecision(12));
    const newPos = (this.pageViewRef.scrollTop - offsets) * diff + offsets;
    this.appliedScrollPosition = Math.round(newPos);
    
    if (scale) {
      this.scale = scale;
      this.displayScale = scale;
    }
    if (scaleValue) this.scaleValue = scaleValue;
    
  }
  
  @action applyPdfScrollPosition() {
    if(!this.appliedScrollPosition)
      return;
    
    this.pageViewRef.scrollTop = this.appliedScrollPosition;
    this.appliedScrollPosition = undefined;
  }

  @action removeDocument() {
    this.pdfDocument = undefined;
    this.allPdfPages = [];
    this.usedPdfPages = [];
    this.currentPage = 0;
    this.totalPages = 0;
    this.scale = 1;
    this.displayScale = 1;
    this.scaleValue = 1;
    this.proposalTablePosition = -1;
  }

  @action removePage(pageId) {
    const index = this.usedPdfPages.find(([p]) => p === pageId)?.[1];
    this.usedPdfPages = this.usedPdfPages.filter((p) => {
      if (p[1] > index) p[1] = p[1] - 1;
      return p[0] !== pageId;
    });

    if (index < this.proposalTablePosition) {
      this.proposalTablePosition = this.proposalTablePosition - 1;
    }

    return {
      usedPages: this.usedPdfPages,
      tablePos: this.proposalTablePosition,
    };
  }

  @action jumpToPage(pageIndex) {
    const element =
      pageIndex === this.proposalTablePosition
        ? document.getElementById(TABLE_CONTAINER_ID)
        : this.pdfPageThumbnails[pageIndex]?.el;
    if (!element) return;
    this.pageViewRef.scrollTop = element.offsetTop - VERTICAL_PADDING;
  }

  @action reorderPages(list) {
    const proposalIndex = list.findIndex((p) => p.id === PROPOSAL_THUMB_ID);

    if (proposalIndex >= 0) {
      this.proposalTablePosition = proposalIndex;
      list.splice(proposalIndex, 1);
    }

    this.usedPdfPages = list.map(({ id }, i) => [id, i]);

    return {
      usedPages: this.usedPdfPages,
      tablePos: this.proposalTablePosition,
    };
  }

  @action findCurrentPage() {
    if (!this.pdfPagesLoaded) return;

    const tableContainer = document.getElementById(TABLE_CONTAINER_ID);
    const containerRect = this.pageViewRef.getBoundingClientRect();
    const pageIndex = this.pdfPageThumbnails.findIndex((p) => {
      const el = p.el || tableContainer;
      if (!el) return;
      const elementRect = el.getBoundingClientRect();
      return elementRect.y + PAGE_VISIBLE_BOTTOM_OFFSET > containerRect.y;
    });

    this.currentPage = pageIndex >= 0 ? pageIndex : this.totalPages;

    if (this.thumbViewRef.current) {
      const thumbContainerRect = this.thumbViewRef.current.getBoundingClientRect();
      const thumbEl =
        this.currentPage === this.proposalTablePosition
          ? tableContainer
          : document.getElementById(`page${this.currentPage}`);

      if (!thumbEl) return;
      if (
        thumbEl.getBoundingClientRect()?.y >
          thumbContainerRect.y + thumbContainerRect.bottom ||
        thumbEl.getBoundingClientRect()?.y < thumbContainerRect.y
      )
        this.thumbViewRef.current.scrollTop = thumbEl.offsetTop - VERTICAL_PADDING;
    }
  }

  @action setUsedPages(usedPdfPages) {
    this.usedPdfPages = usedPdfPages;
  }

  @action setTablePos(tablePos) {
    this.proposalTablePosition = tablePos;
  }
  
  @action async notifyUserAboutComment(shareLink) {
    this.addedCommentThisSession = true;
    await notifyAboutClientCommentQuery(shareLink);
  }
  
  @action setDisplayedComment(comment) {
    if(comment && comment.version === this.currentVersion && this.currentEstimateStructure) {
      this.currentEstimateStructure.openByPath(comment.path);
      setTimeout(() => {
        this.displayedComment = comment;
      }, 500)
    } else {
      this.displayedComment = comment;
    }
  }
  
  @action setAiGenerationStatus(status) {
    this.isAiGenerating = status;
  }
  
  @action setAiGenerationAlert(showAlert) {
    this.showAiGenerationAlert = showAlert;
  }
  
  @action setAiGenerationSuccessAlert(showAlert, fileReaderResults) {
    this.aiGenerationFileStatus = fileReaderResults;
    this.aiGenerationSuccessAlert = showAlert;
  }
  
  @action setAgentSession(session) {
    this.agentSession = session || null;
  }
  
  @action hideUnsupportedBrowserAlert() {
    this.unsupportedBrowserAlert = false;
  }
  
  @action setVersionScroll(scrollTop) {
    this.versionScroll[this.currentVersionKey] = scrollTop;
  }
}

EstimateEditorStoreProvider.propTypes = {
  children: node.isRequired,
  value: instanceOf(EstimateEditorStore).isRequired,
};
